import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { ActorRefFrom } from "xstate";
import type { ApiClient } from "@space/client";
import type { connectivityMachine } from "../state-machines/connectivity-machine";
import { createConnectivityHandlers } from "../auth/controllers";

export interface ConnectivityManagerOptions {
  /** Function to get the current database instance */
  getDb: () => AbstractPowerSyncDatabase | null;
  /** Pre-configured OpenAPI client */
  apiClient: ApiClient;
  /** Reference to the global connectivity state machine actor */
  connectivityRef: ActorRefFrom<typeof connectivityMachine>;
  /** Callback to invalidate app-wide queries when sync settles */
  onInvalidateQueries?: () => void;
  /** Frequency of health checks in milliseconds. Defaults to 15s. */
  healthCheckInterval?: number;
}

const DEFAULT_HEALTH_CHECK_INTERVAL_MS = 15_000;

/**
 * Orchestrates the connectivity state between the network, the API, and PowerSync.
 * This is framework-agnostic and handles:
 * 1. Health polling of the backend API.
 * 2. Monitoring PowerSync status and sync progress.
 * 3. Driving the connectivity state machine.
 */
export function startConnectivityManager(options: ConnectivityManagerOptions) {
  const {
    apiClient,
    connectivityRef,
    onInvalidateQueries,
    healthCheckInterval = DEFAULT_HEALTH_CHECK_INTERVAL_MS,
  } = options;

  const handlers = createConnectivityHandlers((ev) => {
    connectivityRef.send(ev);
  });

  // --- Health Polling ---
  let healthInterval: ReturnType<typeof setInterval> | null = null;
  // Dedupe overlapping probes: a forced foreground re-check (see
  // `forceHealthCheck`) must be safe to call concurrently with the periodic
  // interval — and with itself — without firing two simultaneous pings or
  // racing two `onApiHealthChanged` writes. While a check is in flight, extra
  // callers await the SAME promise.
  let inFlightCheck: Promise<void> | null = null;

  async function runHealthCheck(): Promise<void> {
    try {
      const { data, error } = await apiClient.GET("/api/system/health");
      // A gateway error (e.g. 502 with a non-JSON body) can resolve with both
      // `error` and `data` undefined — guard `data` so we don't throw reading
      // `data.status` and instead correctly report the API as down.
      // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition -- los tipos dicen que `data` siempre existe, pero un 502 sin JSON lo deja undefined en runtime
      if (error || !data) {
        handlers.onApiHealthChanged("down");
      } else {
        handlers.onApiHealthChanged(
          data.status === "maintenance" ? "maintenance" : "up",
          data.version,
          data.engine,
        );
      }
    } catch (err) {
      console.warn("[ConnectivityManager] Health check failed:", err);
      handlers.onApiHealthChanged("down");
    }
  }

  function checkHealth(): Promise<void> {
    inFlightCheck ??= runHealthCheck().finally(() => {
      inFlightCheck = null;
    });
    return inFlightCheck;
  }

  function stopHealthCheck() {
    if (healthInterval) {
      clearInterval(healthInterval);
      healthInterval = null;
    }
  }

  function startHealthCheck(): Promise<void> {
    stopHealthCheck();
    const check = checkHealth();
    healthInterval = setInterval(() => {
      void checkHealth();
    }, healthCheckInterval);
    return check;
  }

  /**
   * Immediately re-probe the API, out of band from the periodic interval.
   *
   * Why this exists: on mobile, sending the tab to the background suspends /
   * throttles the `setInterval` health poll (and any in-flight probe may fail),
   * so `apiStatus` can latch to "down". Returning to the foreground fires no
   * `online` event (the network never actually changed), so nothing forced a
   * re-check and the connectivity machine stayed "down" until a throttled timer
   * eventually ran or the user refreshed. The foreground listener in the web
   * provider calls this to re-probe instantly.
   *
   * When the periodic poll is active we RESTART it around this probe so the
   * cadence realigns after a long background gap (otherwise the next scheduled
   * tick could be far in the future). When polling is stopped (e.g. offline) we
   * just run a one-off, dedup'd check. Safe to call concurrently with the
   * interval — `checkHealth` guards against overlapping in-flight probes.
   */
  function forceHealthCheck(): Promise<void> {
    return healthInterval ? startHealthCheck() : checkHealth();
  }

  // --- PowerSync Status Listener ---
  let unsubscribe: (() => void) | null = null;
  let prevDownloading = false;
  let prevHasSynced = false;

  function setupDbListener(db: AbstractPowerSyncDatabase) {
    if (unsubscribe) {
      unsubscribe();
    }

    handlers.onPowerSyncStatusChanged({
      isConnected: db.connected,
      isConnecting: db.currentStatus.connecting,
      dbError: null,
    });

    unsubscribe = db.registerListener({
      statusChanged: (s) => {
        handlers.onPowerSyncStatusChanged({
          isConnected: s.connected,
          isConnecting: s.connecting,
          dbError: null,
        });

        const { downloading } = s.dataFlowStatus;
        const hasSynced = s.hasSynced ?? false;

        // Force cache invalidation when sync settles (first time or after batch)
        if ((!prevHasSynced && hasSynced) || (prevDownloading && !downloading)) {
          onInvalidateQueries?.();
        }

        prevDownloading = downloading ?? false;
        prevHasSynced = hasSynced;
      },
    });
  }

  return {
    handlers,
    startHealthCheck,
    stopHealthCheck,
    forceHealthCheck,
    setupDbListener,
    dispose: () => {
      stopHealthCheck();
      unsubscribe?.();
    },
  };
}
